import React, {useState} from "react"
import {Button, Form, Input, Alert} from "antd"
import {t} from "@lingui/core/macro"
import {Trans} from "@lingui/react/macro"
import PhoneInput from "../WebEditor/components/PhoneInput"
import {EncheresAPI, poserHint} from "./api"

// S'identifier pour enchérir — en trente secondes, sans créer de compte.
//
// Un fidèle décide d'enchérir le vendredi à 15 h, depuis son téléphone, entre
// deux courses. Tout ce qui ressemble à une inscription le fait renoncer : on
// demande donc le strict nécessaire (de quoi l'appeler et lui écrire), on
// vérifie l'adresse par un code, et son appareil est reconnu ensuite.
//
// Le nom et le téléphone ne sont pas du confort : sans eux, le responsable ne
// peut ni annoncer la montée, ni rattacher l'engagement à quelqu'un.
export default function AuthScreen({identifier, org, lang, onIdentifie, titre, sousTitre}) {
    const [etape, setEtape] = useState("identite")   // identite → code
    const [identite, setIdentite] = useState(null)
    const [erreur, setErreur] = useState(null)
    const [envoi, setEnvoi] = useState(false)
    const [form] = Form.useForm()

    const demanderCode = async (valeurs) => {
        setEnvoi(true)
        setErreur(null)
        try {
            const res = await EncheresAPI.demanderCode(identifier, {email: valeurs.email, lang})
            if (!res || res.success === false) {
                setErreur(t`Cette adresse e-mail ne semble pas valide.`)
                setEnvoi(false)
                return
            }
            setIdentite(valeurs)
            setEtape("code")
        } catch (_) {
            setErreur(t`Le code n'a pas pu être envoyé. Réessayez dans un instant.`)
        } finally {
            setEnvoi(false)
        }
    }

    const verifier = async (valeurs) => {
        setEnvoi(true)
        setErreur(null)
        try {
            const res = await EncheresAPI.verifierCode(identifier, {
                email: identite.email,
                code: String(valeurs.code || "").trim(),
                nom: identite.nom,
                prenom: identite.prenom,
                tel: identite.tel,
                lang,
            })
            if (!res || res.success === false) {
                setErreur(t`Ce code ne correspond pas, ou il a expiré.`)
                setEnvoi(false)
                return
            }
            poserHint(true)
            if (onIdentifie) onIdentifie(res)
        } catch (_) {
            setErreur(t`La vérification a échoué. Réessayez dans un instant.`)
        } finally {
            setEnvoi(false)
        }
    }

    return (
        <div className="ench-auth">
            <div className="ench-auth-titre">{titre || t`Pour enchérir, identifiez-vous`}</div>
            <div className="ench-auth-sous">
                {sousTitre || t`Une fois fait, cet appareil vous reconnaîtra : vous n'aurez plus à le refaire.`}
            </div>

            {erreur && <Alert type="error" showIcon message={erreur} style={{marginBottom: 14}}/>}

            {etape === "identite" ? (
                <Form form={form} layout="vertical" requiredMark={false} onFinish={demanderCode}>
                    <div className="ench-auth-ligne">
                        <Form.Item label={t`Prénom`} name="prenom" rules={[{required: true, message: t`Votre prénom`}]}>
                            <Input autoComplete="given-name" placeholder={t`Prénom`}/>
                        </Form.Item>
                        <Form.Item label={t`Nom`} name="nom" rules={[{required: true, message: t`Votre nom`}]}>
                            <Input autoComplete="family-name" placeholder={t`Nom`}/>
                        </Form.Item>
                    </div>

                    <Form.Item
                        label={t`E-mail`}
                        name="email"
                        rules={[
                            {required: true, message: t`Votre e-mail`},
                            {type: "email", message: t`Cette adresse ne semble pas valide.`},
                        ]}
                        extra={t`C'est là que vous recevrez votre code, puis les nouvelles de vos enchères.`}
                    >
                        <Input autoComplete="email" inputMode="email" placeholder={t`E-mail`}/>
                    </Form.Item>

                    <Form.Item label={t`Téléphone`} name="tel" rules={[{required: true, message: t`Votre téléphone`}]}>
                        <PhoneInput defaultLang={lang} placeholder={t`Téléphone`}/>
                    </Form.Item>

                    <Button type="primary" size="large" block htmlType="submit" loading={envoi}>
                        <Trans>Recevoir mon code</Trans>
                    </Button>
                </Form>
            ) : (
                <Form layout="vertical" requiredMark={false} onFinish={verifier}>
                    <div className="ench-auth-sous" style={{marginBottom: 12}}>
                        <Trans>Un code à 6 chiffres vient de partir vers {identite.email}.</Trans>
                    </div>
                    <Form.Item label={t`Code reçu par e-mail`} name="code" rules={[{required: true, message: t`Le code reçu`}]}>
                        <Input
                            inputMode="numeric"
                            autoComplete="one-time-code"
                            maxLength={6}
                            placeholder="••••••"
                            style={{fontSize: 22, letterSpacing: 6, textAlign: "center"}}
                        />
                    </Form.Item>
                    <Button type="primary" size="large" block htmlType="submit" loading={envoi}>
                        <Trans>Valider</Trans>
                    </Button>
                    <Button type="link" block onClick={() => { setEtape("identite"); setErreur(null) }}>
                        <Trans>Modifier mes informations</Trans>
                    </Button>
                </Form>
            )}
        </div>
    )
}
